import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { Container } from "../components/Container";
import { SEO } from "../components/SEO";
import {
  fetchEsimPackages,
  type EsimPackage,
} from "../lib/api";
import {
  DESTINATIONS_PAGE_SIZE,
  GLOBAL_PACKAGES,
  LOCAL_COUNTRIES,
  REGIONAL_PACKAGES,
  cardClass,
  destinationCardClass,
  errorBoxClass,
  formatDataLabel,
  formatEsimPrice,
  inputClass,
  localizedCountryLabel,
  localizedDestinationName,
  panelClass,
  sortDestinationsWithPriority,
  type EsimDestination,
} from "../lib/esimCatalog";
import { getLocalizedPackageDisplayTitle, getUnlimitedDataLabel } from "../lib/esimFairUsage";
import { useLocale } from "../hooks/useLocale";

type ScopeTab = "country" | "regional" | "global";
type PlanType = "all" | "unlimited" | "fixed" | "callSms";

type DestinationPreview = {
  available: boolean;
  fromPrice?: number;
};

const EMPTY_PREVIEW: DestinationPreview = { available: false };

const DURATION_PRESETS = [3, 7, 15, 30] as const;

const PLAN_TYPES: PlanType[] = ["all", "unlimited", "fixed", "callSms"];

const filterChipClass = (active: boolean) =>
  `rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
    active
      ? "bg-brand-500 text-white shadow-sm"
      : "bg-bg-muted text-text-muted hover:bg-bg-subtle hover:text-text-heading dark:bg-bg-base dark:hover:bg-bg-muted"
  }`;

function previewKey(item: EsimDestination) {
  return `${item.geography}:${item.code}`;
}

function previewParams(item: EsimDestination) {
  if (item.geography === "regional") {
    return { regionalPackage: item.code };
  }
  return { countryCode: item.code };
}
export function EsimShop() {
  const { t } = useTranslation();
  const { pathFor, language } = useLocale();
  const navigate = useNavigate();

  const labelOf = (item: EsimDestination) =>
    localizedDestinationName(item, {
      locale: language.acceptLanguageCode,
      t,
    });

  const [scope, setScope] = useState<ScopeTab>("country");
  const [destination, setDestination] = useState<EsimDestination | null>(null);
  const [packages, setPackages] = useState<EsimPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destinationSearch, setDestinationSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(DESTINATIONS_PAGE_SIZE);
  const [packageSearch, setPackageSearch] = useState("");
  const [planType, setPlanType] = useState<PlanType>("all");
  const [durationDays, setDurationDays] = useState<number | "all">("all");
  const [availableDays, setAvailableDays] = useState<number[]>([]);
  const [destinationPreviews, setDestinationPreviews] = useState<
    Record<string, DestinationPreview>
  >({});
  const previewLoadedRef = useRef(new Set<string>());
  const previewInflightRef = useRef(new Set<string>());

  const loadDestinationPreview = useCallback(async (item: EsimDestination) => {
    const key = previewKey(item);
    if (previewLoadedRef.current.has(key) || previewInflightRef.current.has(key)) {
      return;
    }
    previewInflightRef.current.add(key);
    try {
      const response = await fetchEsimPackages({
        ...previewParams(item),
        page: 0,
        pageSize: 1,
        sortBy: "price",
        sortDesc: false,
      });
      const pkg = response.packages?.[0];
      previewLoadedRef.current.add(key);
      if (pkg && Number.isFinite(pkg.price)) {
        setDestinationPreviews((prev) => ({
          ...prev,
          [key]: { available: true, fromPrice: pkg.price },
        }));
      } else {
        setDestinationPreviews((prev) => ({ ...prev, [key]: EMPTY_PREVIEW }));
      }
    } catch {
      previewLoadedRef.current.add(key);
      setDestinationPreviews((prev) => ({ ...prev, [key]: EMPTY_PREVIEW }));
    } finally {
      previewInflightRef.current.delete(key);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setDestination(null);
    setPackages([]);
    setError(null);
    setDestinationSearch("");
    setVisibleCount(DESTINATIONS_PAGE_SIZE);
  }, [scope]);

  useEffect(() => {
    setVisibleCount(DESTINATIONS_PAGE_SIZE);
  }, [destinationSearch]);

  useEffect(() => {
    if (!destination) return;
    if (destination.geography === "local") {
      void loadPackages({ countryCode: destination.code });
    } else if (destination.geography === "regional") {
      void loadPackages({ regionalPackage: destination.code });
    } else {
      void loadPackages({ countryCode: destination.code });
    }
  }, [destination, planType, durationDays, packageSearch]);

  function openDestination(item: EsimDestination) {
    setPackageSearch("");
    setPlanType("all");
    setDurationDays("all");
    setAvailableDays([]);
    setPackages([]);
    setError(null);
    setDestination(item);
  }

  function closeDestination() {
    setDestination(null);
    setPackages([]);
    setError(null);
    setPackageSearch("");
    setPlanType("all");
    setDurationDays("all");
    setAvailableDays([]);
  }

  async function loadPackages(scopeParams: {
    countryCode?: string;
    regionalPackage?: string;
    geography?: string;
  }) {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof fetchEsimPackages>[0] = {
        ...scopeParams,
        page: 0,
        pageSize: 50,
        filter: packageSearch.trim() || undefined,
        sortBy: "price",
        sortDesc: false,
      };

      if (planType === "unlimited") params.isUnlimited = true;
      if (planType === "fixed") params.isUnlimited = false;
      if (planType === "callSms") {
        params.withCall = true;
        params.withSMS = true;
      }
      if (durationDays !== "all") {
        params.minDays = durationDays;
        params.maxDays = durationDays;
      }

      const data = await fetchEsimPackages(params);
      setPackages(data.packages ?? []);
      setAvailableDays(
        (data.availableDurationDays ?? []).filter((d) => d !== 1).sort((a, b) => a - b),
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("esim.load_packages_error"),
      );
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }

  const allDestinations = useMemo(() => {
    if (scope === "country") return LOCAL_COUNTRIES;
    if (scope === "regional") return REGIONAL_PACKAGES;
    return GLOBAL_PACKAGES;
  }, [scope]);

  const filteredDestinations = useMemo(
    () => {
      const q = destinationSearch.trim().toLowerCase();
      const base =
        scope === "country"
          ? sortDestinationsWithPriority(allDestinations, "")
          : [...allDestinations];

      const filtered = !q
        ? base
        : base.filter((c) => {
            const localized = labelOf(c).toLowerCase();
            return (
              localized.includes(q) ||
              c.name.toLowerCase().includes(q) ||
              c.code.toLowerCase().includes(q) ||
              c.region.toLowerCase().includes(q)
            );
          });

      // Keep priority pins first for country tab; otherwise sort by localized label.
      if (scope !== "country" || q) {
        return filtered
          .slice()
          .sort((a, b) => labelOf(a).localeCompare(labelOf(b), language.acceptLanguageCode));
      }
      return filtered;
    },
    [allDestinations, destinationSearch, scope, language.acceptLanguageCode, t],
  );

  const visibleDestinations = useMemo(
    () => filteredDestinations.slice(0, visibleCount),
    [filteredDestinations, visibleCount],
  );

  // Same as mobile: cheapest package (sortBy=price, pageSize=1) for visible cards.
  useEffect(() => {
    if (destination) return;

    let cancelled = false;

    async function run() {
      const needs = visibleDestinations.filter(
        (item) => !previewLoadedRef.current.has(previewKey(item)),
      );
      for (let i = 0; i < needs.length; i += 4) {
        if (cancelled) return;
        const batch = needs.slice(i, i + 4);
        await Promise.all(batch.map((item) => loadDestinationPreview(item)));
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [destination, visibleDestinations, loadDestinationPreview]);

  const hasMoreDestinations = visibleCount < filteredDestinations.length;

  const otherDurationDays = useMemo(
    () =>
      availableDays.filter(
        (day) => !(DURATION_PRESETS as readonly number[]).includes(day),
      ),
    [availableDays],
  );

  const filtersActive =
    packageSearch.trim() !== "" ||
    planType !== "all" ||
    durationDays !== "all";

  const clearFilters = () => {
    setPackageSearch("");
    setPlanType("all");
    setDurationDays("all");
  };

  const scopeLabel = t(`esim.tab_${scope}`);
  const destinationCountKey =
    filteredDestinations.length === 1
      ? "esim.destination_count"
      : "esim.destination_count_plural";

  return (
    <main className="min-h-screen bg-bg-base pt-28 sm:pt-32">
      <SEO
        title={t("esim.meta_title_shop")}
        description={t("esim.meta_description_shop")}
        path="/esim/shop"
      />

      <Container size="lg">
        <article className="py-12 sm:py-16">
          <header className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t("esim.eyebrow")}
            </p>
            <h1 className="hero-headline text-text-heading">
              {t("esim.shop_title")}
            </h1>
            <p className="mt-4 text-lg text-text-muted">{t("esim.shop_subtitle")}</p>
          </header>

          <div className="mt-8 flex flex-wrap gap-2">
            {(["country", "regional", "global"] as ScopeTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setScope(tab)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  scope === tab
                    ? "bg-brand-500 text-white"
                    : "bg-bg-muted text-text-muted hover:text-text-heading dark:bg-bg-subtle"
                }`}
              >
                {t(`esim.tab_${tab}`)}
              </button>
            ))}
          </div>

          {!destination ? (
            <div className="mt-8 space-y-4">
              <div className={panelClass}>
                <label className="grid gap-1 text-sm">
                  <span className="font-medium text-text-heading">
                    {scope === "country"
                      ? t("esim.search_countries")
                      : t("esim.search_destinations")}
                  </span>
                  <input
                    value={destinationSearch}
                    onChange={(e) => setDestinationSearch(e.target.value)}
                    placeholder={
                      scope === "country"
                        ? t("esim.search_country_placeholder")
                        : t("esim.search_destination_placeholder")
                    }
                    className={inputClass}
                  />
                </label>
                <p className="mt-2 text-xs text-text-muted">
                  {t(destinationCountKey, { count: filteredDestinations.length })}
                </p>
              </div>

              {filteredDestinations.length === 0 ? (
                <p className="text-text-muted">{t("esim.no_destinations")}</p>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {visibleDestinations.map((item) => (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => openDestination(item)}
                        className={destinationCardClass}
                      >
                        <span className="esim-dest-card-glow" aria-hidden="true" />
                        <span className="esim-dest-card-accent" aria-hidden="true" />

                        <div className="relative flex items-start gap-3.5">
                          <span className="esim-dest-flag-wrap">
                            <img
                              src={item.flag}
                              alt=""
                              width={48}
                              height={36}
                              loading="lazy"
                              className="esim-dest-flag"
                              onError={(e) => {
                                const img = e.currentTarget;
                                if (img.dataset.fallback === "1") return;
                                img.dataset.fallback = "1";
                                img.src = "https://flagcdn.com/w80/un.png";
                              }}
                            />
                          </span>
                          <div className="min-w-0 flex-1 pt-0.5">
                            <h2 className="truncate text-[1.05rem] font-semibold tracking-tight text-text-heading">
                              {labelOf(item)}
                            </h2>
                            <p className="mt-1 truncate text-xs font-medium tracking-wide text-text-subtle">
                              {item.region}
                              {item.code ? (
                                <>
                                  <span className="mx-1.5 text-border-strong">·</span>
                                  <span className="uppercase text-text-muted">
                                    {item.code}
                                  </span>
                                </>
                              ) : null}
                            </p>
                            {(() => {
                              const preview = destinationPreviews[previewKey(item)];
                              if (!preview) {
                                return (
                                  <p className="esim-dest-price-skeleton mt-2.5 h-4 w-28 rounded-full" />
                                );
                              }
                              if (
                                !preview.available ||
                                preview.fromPrice == null ||
                                !Number.isFinite(preview.fromPrice)
                              ) {
                                return (
                                  <p className="mt-2.5 text-xs font-medium text-text-subtle">
                                    {t("esim.not_available")}
                                  </p>
                                );
                              }
                              return (
                                <p className="mt-2.5 text-sm text-text-heading">
                                  <span className="text-text-muted">
                                    {t("esim.starting_from")}{" "}
                                  </span>
                                  <span className="font-semibold text-brand-600 dark:text-brand-400">
                                    {formatEsimPrice(preview.fromPrice)}
                                  </span>
                                </p>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="relative mt-5 flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-brand-500 transition group-hover:text-brand-600">
                            {t("esim.view_plans")}
                          </span>
                          <span
                            className="esim-dest-arrow inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand-500 transition duration-300 group-hover:bg-brand-500 group-hover:text-white dark:bg-brand-950/40"
                            aria-hidden="true"
                          >
                            <svg
                              viewBox="0 0 20 20"
                              className="h-4 w-4 transition duration-300 group-hover:translate-x-0.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4 10h10m0 0l-3.5-3.5M14 10l-3.5 3.5"
                              />
                            </svg>
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {hasMoreDestinations ? (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleCount((n) =>
                            Math.min(
                              n + DESTINATIONS_PAGE_SIZE,
                              filteredDestinations.length,
                            ),
                          )
                        }
                        className="rounded-full border border-border-soft bg-bg-muted px-5 py-2 text-sm font-semibold text-text-heading transition hover:border-brand-500/40 dark:bg-bg-subtle"
                      >
                        {t("esim.show_more", {
                          count: filteredDestinations.length - visibleCount,
                        })}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {destination ? (
            <div className="mt-8 space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={closeDestination}
                  className="text-sm font-semibold text-brand-500"
                >
                  {t("esim.back_all_destinations", { scope: scopeLabel })}
                </button>
                <div className="flex items-center gap-2">
                  <img
                    src={destination.flag}
                    alt=""
                    width={32}
                    height={22}
                    className="h-[22px] w-8 rounded object-cover ring-1 ring-border-soft"
                  />
                  <h2 className="text-xl font-semibold text-text-heading">
                    {labelOf(destination)}
                  </h2>
                </div>
              </div>

              <div className={`${panelClass} space-y-4`}>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                    aria-hidden
                  />
                  <input
                    value={packageSearch}
                    onChange={(e) => setPackageSearch(e.target.value)}
                    placeholder={t("esim.package_name_placeholder")}
                    aria-label={t("esim.search_packages")}
                    className={`${inputClass} ps-9 pe-9`}
                  />
                  {packageSearch ? (
                    <button
                      type="button"
                      onClick={() => setPackageSearch("")}
                      className="absolute end-2.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-text-muted transition hover:bg-bg-muted hover:text-text-heading"
                      aria-label={t("esim.clear_search", {
                        defaultValue: "Clear search",
                      })}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : null}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="me-1 text-xs font-bold uppercase tracking-[0.16em] text-text-muted">
                      {t("esim.plan_type")}
                    </span>
                    {PLAN_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setPlanType(type)}
                        className={filterChipClass(planType === type)}
                      >
                        {type === "all"
                          ? t("esim.plan_type_all")
                          : type === "unlimited"
                            ? t("esim.plan_type_unlimited")
                            : type === "fixed"
                              ? t("esim.plan_type_fixed")
                              : t("esim.plan_type_call_sms")}
                      </button>
                    ))}
                  </div>

                  {filtersActive ? (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="self-start text-sm font-semibold text-brand-500 hover:underline sm:self-auto"
                    >
                      {t("esim.clear_filters", {
                        defaultValue: "Clear filters",
                      })}
                    </button>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-border-soft pt-4">
                  <span className="me-1 text-xs font-bold uppercase tracking-[0.16em] text-text-muted">
                    {t("esim.duration")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDurationDays("all")}
                    className={filterChipClass(durationDays === "all")}
                  >
                    {t("esim.all")}
                  </button>
                  {DURATION_PRESETS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => setDurationDays(day)}
                      className={filterChipClass(durationDays === day)}
                    >
                      {t("esim.days", { count: day })}
                    </button>
                  ))}
                  {otherDurationDays.length > 0 ? (
                    <label className="inline-flex items-center gap-2">
                      <span className="sr-only">
                        {t("esim.more_durations", {
                          defaultValue: "More durations",
                        })}
                      </span>
                      <select
                        value={
                          typeof durationDays === "number" &&
                          otherDurationDays.includes(durationDays)
                            ? String(durationDays)
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setDurationDays("all");
                            return;
                          }
                          setDurationDays(Number(value));
                        }}
                        className={`h-9 rounded-full border px-3.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                          typeof durationDays === "number" &&
                          otherDurationDays.includes(durationDays)
                            ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/20 dark:text-brand-300"
                            : "border-border-soft bg-bg-muted text-text-muted dark:bg-bg-base"
                        }`}
                      >
                        <option value="">
                          {t("esim.more_durations", {
                            defaultValue: "More…",
                          })}
                        </option>
                        {otherDurationDays.map((day) => (
                          <option key={day} value={day}>
                            {t("esim.days", { count: day })}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>

                {!loading && !error ? (
                  <p className="text-xs text-text-muted">
                    {packages.length === 1
                      ? t("esim.results_count", {
                          count: packages.length,
                          defaultValue: "{{count}} plan",
                        })
                      : t("esim.results_count_plural", {
                          count: packages.length,
                          defaultValue: "{{count}} plans",
                        })}
                  </p>
                ) : null}
              </div>

              {loading ? (
                <p className="text-text-muted">{t("esim.loading_packages")}</p>
              ) : error ? (
                <p className={errorBoxClass}>{error}</p>
              ) : packages.length === 0 ? (
                <p className="text-text-muted">{t("esim.no_packages")}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {packages.map((pkg) => {
                    const dataLabel = pkg.isUnlimited
                      ? getUnlimitedDataLabel(pkg, t)
                      : formatDataLabel(pkg);
                    const title = getLocalizedPackageDisplayTitle(pkg, t);
                    return (
                      <button
                        key={pkg.packageId}
                        type="button"
                        onClick={() =>
                          navigate(
                            pathFor(
                              `/esim/package?packageId=${encodeURIComponent(pkg.packageId)}`,
                            ),
                            { state: { package: pkg } },
                          )
                        }
                        className={cardClass}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="rounded-full bg-brand-500/10 px-2.5 py-1 text-xs font-bold text-brand-500 dark:bg-brand-500/20">
                            {dataLabel}
                          </span>
                          <span className="text-lg font-bold text-text-heading">
                            {formatEsimPrice(pkg.price)}
                          </span>
                        </div>
                        <h3 className="mt-3 font-semibold text-text-heading">
                          {title}
                        </h3>
                        <p className="mt-1 text-sm text-text-muted">
                          {t("esim.package_meta", {
                            place:
                              localizedCountryLabel(
                                pkg.countryCode,
                                pkg.countryName,
                                {
                                  locale: language.acceptLanguageCode,
                                  t,
                                  geography: pkg.geography,
                                },
                              ) || labelOf(destination),
                            days: pkg.durationDays,
                          })}
                          {pkg.networks?.[0] ? ` · ${pkg.networks[0]}` : ""}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {pkg.withHotspot ? (
                            <Chip>{t("esim.chip_hotspot")}</Chip>
                          ) : null}
                          {pkg.withDataRoaming ? (
                            <Chip>{t("esim.chip_roaming")}</Chip>
                          ) : null}
                          {pkg.withTopUp ? (
                            <Chip>{t("esim.chip_topup")}</Chip>
                          ) : null}
                        </div>
                        <p className="mt-4 text-sm font-semibold text-brand-500">
                          {t("esim.view_details")}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <p className="mt-10 text-sm text-text-muted">
            {t("esim.prefer_app")}{" "}
            <Link to="/esim" className="font-semibold text-brand-500 underline">
              {t("esim.open_in_app")}
            </Link>
          </p>
        </article>
      </Container>
    </main>
  );
}

function Chip({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-bg-muted px-2 py-0.5 text-[11px] font-medium text-text-muted dark:bg-bg-base">
      {children}
    </span>
  );
}
